import { GridRowId } from "@mui/x-data-grid-pro";
import { createContext, useContext } from "react";
import { To, useHref, useNavigate } from "react-router-dom";

/** Where each row of a table links to, provided only by tables whose rows are links */
const RowLinksContext = createContext<
  ReadonlyMap<string, To | undefined> | undefined
>(undefined);

/** Provides the links of the rows, so that the row slot can render them */
export const RowLinksProvider = RowLinksContext.Provider;

/** Returns where the row links to, or undefined when it is not a link */
export function useRowLink(rowId: GridRowId): To | undefined {
  return useContext(RowLinksContext)?.get(String(rowId));
}

/** Elements that handle a click themselves, either natively or through a role */
const INTERACTIVE_ELEMENT_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "label",
  "select",
  "textarea",
  "[role='button']",
  "[role='checkbox']",
  "[role='combobox']",
  "[role='link']",
  "[role='menuitem']",
  "[role='option']",
  "[role='switch']",
].join(",");

/** Elements the browser activates on a click instead of an anchor around them */
const NATIVELY_ACTIVATED_ELEMENT_SELECTOR =
  "a[href], button, input, label, select, textarea";

/**
 * Whether the click is meant for a control inside the row, like a chip or a menu, rather than for
 * the row. Clicks inside a portal, like a menu opened from the row, bubble to the row through React
 * although they happen outside of it.
 */
function isClickForNestedControl(
  target: EventTarget,
  rowLink: HTMLAnchorElement
): boolean {
  if (!(target instanceof Element) || !rowLink.contains(target)) {
    return true;
  }

  const control = target.closest(INTERACTIVE_ELEMENT_SELECTOR);
  return control !== null && control !== rowLink;
}

/** Whether the browser should handle the click, e.g. to open the link in a new tab */
function isClickForBrowser(event: React.MouseEvent): boolean {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

interface DataGridRowLinkProps {
  /** Where the row links to */
  to: To;

  /** The row */
  children: React.ReactNode;
}

/**
 * Makes a row a link, so that it can also be opened in a new tab and shows where it leads.
 * The link generates no box of its own, so the row keeps its place in the layout of the grid.
 */
export function DataGridRowLink({
  to,
  children,
}: DataGridRowLinkProps): React.JSX.Element {
  const href = useHref(to);
  const navigate = useNavigate();

  /**
   * A control inside the row that the browser does not activate itself, like a chip, would
   * otherwise also follow the link, even when it stops the propagation of the click
   */
  function onClickCapture(event: React.MouseEvent<HTMLAnchorElement>): void {
    if (!isClickForNestedControl(event.target, event.currentTarget)) {
      return;
    }

    const activatedElement =
      event.target instanceof Element
        ? event.target.closest(NATIVELY_ACTIVATED_ELEMENT_SELECTOR)
        : null;
    if (activatedElement === event.currentTarget) {
      event.preventDefault();
    }
  }

  function onClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    if (
      event.defaultPrevented ||
      isClickForNestedControl(event.target, event.currentTarget) ||
      isClickForBrowser(event)
    ) {
      return;
    }

    event.preventDefault();
    navigate(to);
  }

  return (
    <a
      href={href}
      onClickCapture={onClickCapture}
      onClick={onClick}
      // The grid handles the keyboard focus of its cells, the link would be an extra tab stop
      tabIndex={-1}
      // Dragging over the row selects its text instead of dragging the link
      draggable={false}
      style={{ display: "contents", color: "inherit", textDecoration: "none" }}
    >
      {children}
    </a>
  );
}
